import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getContractSource, verifyContract } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { FileCode2, ShieldCheck, Loader2, Copy } from "lucide-react";
import { toast } from "sonner";

export const useContractSource = (address) =>
  useQuery({ queryKey: ["contractSource", address], queryFn: () => getContractSource(address), retry: false });

const SourceView = ({ data }) => {
  const [tab, setTab] = useState("source");
  const text = tab === "source" ? data.source : JSON.stringify(data.abi, null, 2);
  const copy = () => {
    navigator.clipboard.writeText(text);
    toast.success(tab === "source" ? "Source copied" : "ABI copied");
  };
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
        {[["Contract", data.contractName], ["Compiler", `solc ${data.compilerVersion}`], ["Optimizer", data.optimizer ? `Yes (${data.runs} runs)` : "No"], ["EVM", data.evmVersion]].map(([k, v]) => (
          <div key={k}><p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">{k}</p><p className="text-white font-medium">{v}</p></div>
        ))}
      </div>
      <div className="flex items-center gap-2">
        {["source", "abi"].map((t) => (
          <button key={t} data-testid={`source-tab-${t}`} onClick={() => setTab(t)}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold ${tab === t ? "bg-[#D4AF37] text-black" : "text-zinc-400 hover:text-white"}`}>
            {t === "source" ? "Source code" : "ABI"}
          </button>
        ))}
        <button data-testid="source-copy-btn" onClick={copy} className="ml-auto text-xs text-zinc-400 hover:text-[#D4AF37] flex items-center gap-1"><Copy size={12} /> Copy</button>
      </div>
      <pre data-testid="contract-source-code" className="max-h-[480px] overflow-auto p-4 rounded-md bg-[#050505] border border-[#2A2411] text-xs leading-relaxed text-zinc-300">{text}</pre>
    </div>
  );
};

const VerifyForm = ({ address }) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ contractName: "", source: "", optimizer: false, runs: 200, evmVersion: "paris" });
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setErrors(null);
    setBusy(true);
    try {
      await verifyContract(address, { ...form, runs: Number(form.runs) });
      toast.success("Contract source verified");
      queryClient.invalidateQueries({ queryKey: ["contractSource", address] });
    } catch (err) {
      const d = err.response?.data?.detail;
      setErrors(typeof d === "string" ? { message: d, errors: [] } : d || { message: "Verification failed", errors: [] });
    } finally {
      setBusy(false);
    }
  };

  const field = "bg-[#050505] border-[#2A2411] text-white placeholder:text-zinc-600";
  return (
    <form onSubmit={submit} data-testid="verify-form" className="space-y-4">
      <p className="text-sm text-zinc-400">Paste the Solidity source. It's compiled with solc 0.8.24 and must match the deployed code exactly. You can import OpenZeppelin Contracts v5; flatten any other imports into one file.</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Input data-testid="verify-contract-name" placeholder="Contract name, e.g. StarterToken" value={form.contractName}
          onChange={(e) => setForm({ ...form, contractName: e.target.value.trim() })} className={field} />
        <select data-testid="verify-evm-version" value={form.evmVersion} onChange={(e) => setForm({ ...form, evmVersion: e.target.value })}
          className={`h-9 rounded-md border px-3 text-sm ${field}`}>
          {["paris", "shanghai", "cancun", "london"].map((v) => <option key={v} value={v}>EVM: {v}</option>)}
        </select>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-zinc-300">
            <Checkbox data-testid="verify-optimizer" checked={form.optimizer} onCheckedChange={(v) => setForm({ ...form, optimizer: Boolean(v) })} /> Optimizer
          </label>
          {form.optimizer && <Input data-testid="verify-runs" type="number" value={form.runs} onChange={(e) => setForm({ ...form, runs: e.target.value })} className={`w-24 ${field}`} />}
        </div>
      </div>
      <Textarea data-testid="verify-source-input" rows={12} placeholder="// SPDX-License-Identifier: MIT&#10;pragma solidity ^0.8.20;&#10;..." value={form.source}
        onChange={(e) => setForm({ ...form, source: e.target.value })} className={`font-mono text-xs ${field}`} />
      {errors && (
        <div data-testid="verify-error" className="p-3 rounded-md bg-red-500/5 border border-red-500/20 text-red-400 text-xs space-y-1">
          <p className="font-medium">{errors.message}</p>
          {errors.errors?.map((m, i) => <pre key={i} className="whitespace-pre-wrap">{m}</pre>)}
        </div>
      )}
      <Button data-testid="verify-submit-btn" type="submit" disabled={busy || !form.contractName || form.source.length < 10}
        className="bg-[#D4AF37] text-black hover:bg-[#F3E5AB] font-semibold">
        {busy ? <><Loader2 size={14} className="animate-spin mr-2" /> Compiling & matching…</> : <><ShieldCheck size={14} className="mr-2" /> Verify & publish source</>}
      </Button>
    </form>
  );
};

export const ContractSource = ({ address }) => {
  const { data, isLoading } = useContractSource(address);
  if (isLoading) return null;
  return (
    <Card data-testid="contract-source" className="border-[#2A2411] bg-[#0A0A0A] mt-6">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-display flex items-center gap-2">
          <FileCode2 size={16} className="text-[#D4AF37]" /> Contract Source
          {data && <span data-testid="contract-verified-label" className="ml-2 text-xs font-semibold text-green-400 flex items-center gap-1"><ShieldCheck size={13} /> Verified</span>}
        </CardTitle>
      </CardHeader>
      <CardContent>{data ? <SourceView data={data} /> : <VerifyForm address={address} />}</CardContent>
    </Card>
  );
};
